import type { Attribute } from '../sdk/attributes.ts'
import type { Table } from '../sdk/search.ts'
import type { Bucket, Window } from '../lib/time.ts'
import { createSearchCache } from '../sdk/search.ts'
import { DL_TO_L, FLOW_ATTRS, resolveFlowAttrs, tableCovers, type FlowAttrKey } from './flow.ts'
import type { SeriesSource } from './aggregate.ts'

export interface FetchProgress {
  label: string
  rows: number
  done: number
  total: number
}

export interface FetchedFlow {
  sources: Partial<Record<FlowAttrKey, SeriesSource>>
  tables: { key: string; attr: Attribute; table: Table; period: string | null }[]
  notes: string[]
  resolution: ReturnType<typeof resolveFlowAttrs>
}

/** Server-side aggregation when asked for and the attribute offers this bucket, else raw rows. */
export function periodFor(attr: Attribute, bucket: Bucket, serverPeriod: boolean): string | null {
  return serverPeriod && attr.periods.includes(bucket) ? bucket : null
}

/**
 * Fetch the flow attributes a report needs.
 *
 * `reuseRecords` exploits the fact that a reply is the whole record: a fetch
 * for expected volume usually already carries measured and unexpected, so the
 * same megabytes need not be fetched three times. It is off by default —
 * separate fetches are unambiguously correct, and the saving is only worth
 * taking once the numbers have been checked against it.
 */
export async function fetchFlow(
  interfaceId: number,
  window: Window,
  bucket: Bucket,
  attrs: Attribute[],
  opts: { reuseRecords?: boolean; serverPeriod?: boolean; onProgress?: (p: FetchProgress) => void } = {},
): Promise<FetchedFlow> {
  const resolution = resolveFlowAttrs(attrs)
  const cache = createSearchCache()
  const sources: Partial<Record<FlowAttrKey, SeriesSource>> = {}
  const tables: FetchedFlow['tables'] = []
  const notes: string[] = []

  // Expected-by-rule first: it is the record most likely to carry the others.
  const order: FlowAttrKey[] = ['ruleExpected', 'ruleUnexpected', 'ruleMeasured', 'globalExpected']
  const wanted = order.filter((k) => resolution.resolved[k]?.available)
  let done = 0

  for (const key of wanted) {
    const attr = resolution.resolved[key]!
    const column = FLOW_ATTRS[key].name

    if (opts.reuseRecords) {
      const holder = tables.find((t) => t.period === periodFor(attr, bucket, opts.serverPeriod !== false) && tableCovers(t.table, column))
      if (holder) {
        sources[key] = { key, label: attr.desc, column, table: holder.table, sourceKey: holder.key, scale: DL_TO_L }
        notes.push(`${attr.desc} read from the ${holder.attr.desc} record — no extra fetch.`)
        done++
        continue
      }
    }

    const period = periodFor(attr, bucket, opts.serverPeriod !== false)
    opts.onProgress?.({ label: attr.desc, rows: 0, done, total: wanted.length })
    const table = await cache.fetch(
      interfaceId,
      window,
      { attribute: attr.searchId, period },
      (rows) => opts.onProgress?.({ label: attr.desc, rows, done, total: wanted.length }),
    )
    if (table.truncated) notes.push(`${attr.desc}: hit the page-count guard — the period may be incomplete.`)
    tables.push({ key, attr, table, period })
    sources[key] = { key, label: attr.desc, column, table, sourceKey: key, scale: DL_TO_L }
    done++
  }

  for (const key of resolution.unavailable) {
    notes.push(`${FLOW_ATTRS[key].desc}: not collected for this account (no subscription).`)
  }
  for (const key of resolution.missing) {
    notes.push(`${FLOW_ATTRS[key].desc}: the cloud does not offer this attribute.`)
  }
  for (const r of resolution.renumbered) {
    notes.push(
      `${FLOW_ATTRS[r.key].desc} now has search id ${r.actual} (was ${r.expected}) — resolved by name, as intended.`,
    )
  }
  return { sources, tables, notes, resolution }
}
